import React, { ChangeEvent } from 'react';
import { NumericFormat, NumericFormatProps, InputAttributes } from 'react-number-format';

import { Format } from '@homesusa/core';
import { TextField } from './textfield.component';
import { NumberInputProps, NumberFormat as HusaNumberFormat } from '../../interfaces';
import * as Valid from '../../validations';
import { useValidateInput, useFormContext, useGetFieldRules } from '../../hooks';
import { TooltipContainer } from '../tooltip-container';

export function NumberInput({ name, ...props }: NumberInputProps): JSX.Element {
  const contextRules = useGetFieldRules<NumberInputProps>(name);
  return <NumberInputComponent {...{ ...contextRules, ...props }} name={name} />;
}

const NumberFormatCustom = React.forwardRef<
  NumericFormatProps<InputAttributes>,
  {
    onChange: (event: { target: { name: string; value: string } }) => void;
    name: string;
    numberFormat?: HusaNumberFormat;
  }
>(function NumberFormatCustom(props, ref) {
  const { onChange, numberFormat, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values: { value: string }): void => {
        onChange({
          target: {
            name: props.name,
            value: values.value
          }
        });
      }}
      allowNegative={numberFormat?.min == null || numberFormat?.min < 0}
      valueIsNumericString
      {...numberFormat}
      decimalScale={numberFormat?.decimalScale ?? 0}
    />
  );
});

function NumberInputComponent(props: NumberInputProps): JSX.Element {
  const getError = (value: unknown): string[] | null => {
    const error = Valid.getTextFieldError(value, props, 'digits');
    if (error) {
      return [error];
    }

    if (props.numberFormat == null) {
      return null;
    }

    const errorMessages = props.customFieldProps?.errorMessages;
    if (!Valid.checkMinLimit(value, props.numberFormat.min)) {
      return [
        errorMessages?.min ??
          `Must be ${
            props.numberFormat.prefix === '$'
              ? Format.Money(props.numberFormat.min)
              : props.numberFormat.thousandSeparator
                ? Format.Number(props.numberFormat.min)
                : props.numberFormat.min
          } ${props.isDate ? 'or later' : 'or more'}`
      ];
    }
    if (!Valid.checkMaxLimit(value, props.numberFormat.max)) {
      return [
        errorMessages?.max ??
          `Must be ${
            props.numberFormat.prefix === '$'
              ? Format.Money(props.numberFormat.max)
              : props.numberFormat.thousandSeparator
                ? Format.Number(props.numberFormat.max)
                : props.numberFormat.max
          } ${props.isDate ? 'or earlier' : 'or less'}`
      ];
    }
    return null;
  };

  const { error, validateInput } = useValidateInput(props, getError);
  const { setValueByName } = useFormContext();
  const allProps = { ...props, tooltip: '' };
  const setValue = (newValue: string): void => {
    const value = Valid.isNullOrEmpty(newValue) ? null : newValue;
    setValueByName(props.name, value);
    if (props.onChange) {
      props.onChange(value ?? undefined);
    }
    validateInput(value);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => setValue(event.target.value);

  React.useEffect(() => {
    if (props.defaultValue != null && props.defaultValue !== '') {
      setValue(`${props.defaultValue}`);
    }
    if (props.defaultValue !== undefined) validateInput(props.defaultValue);
  }, []);

  return (
    <TooltipContainer tooltip={props.tooltip}>
      <TextField
        {...allProps}
        muiProps={{
          size: allProps.size ?? 'medium',
          name: props.name,
          value: props.value,
          onChange: handleChange,
          helperText: error ? error : props.helperText,
          error: error != null,
          InputProps: {
            inputComponent: NumberFormatCustom as any,
            inputProps: {
              numberFormat: props.numberFormat
            }
          }
        }}
      />
    </TooltipContainer>
  );
}
